import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router, ActivatedRoute } from '@angular/router';

import { PageHeaderComponent } from '../../../shared/page-header/page-header.component';
import { MatInputComponent } from '../../../shared/mat-input/mat-input.component';
import { TripData } from '../../../models/billing.model';
import {
  BillingService,
  InvoiceRequest,
} from '../../../service/billing.service';

interface AddonItem {
  id: string;
  name: string;
  amount: number;
}

interface DiscountItem {
  id: string;
  name: string;
  amount: number;
}

@Component({
  selector: 'app-bill-generation',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatSnackBarModule,
    MatInputModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    PageHeaderComponent,
    MatInputComponent,
  ],
  templateUrl: './bill-generation.component.html',
  styleUrls: ['./bill-generation.component.scss'],
})
export class BillGenerationComponent implements OnInit {
  selectedTrips: TripData[] = [];
  vendorDetails: any = null;
  filters: any = {};
  subtotal: number = 0;

  addonForm: FormGroup;
  discountForm: FormGroup;

  addons: AddonItem[] = [];
  discounts: DiscountItem[] = [];

  totalAmount: number = 0;
  isGenerating = false;

  constructor(
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private router: Router,
    private route: ActivatedRoute,
    private billingService: BillingService
  ) {
    this.addonForm = this.fb.group({
      name: ['', Validators.required],
      amount: [0, [Validators.required, Validators.min(0)]],
    });

    this.discountForm = this.fb.group({
      name: ['', Validators.required],
      amount: [0, [Validators.required, Validators.min(0)]],
    });
  }

  ngOnInit(): void {
    // Get data from router state using history.state
    const state = history.state;
    if (state && state.selectedTrips) {
      this.selectedTrips = state.selectedTrips || [];
      this.vendorDetails = state.vendor || null;
      this.filters = state.filters || {};
      console.log(
        'Bill generation - Data received from router state:',
        this.selectedTrips
      );
    } else {
      // Fallback: try to get from query params (for backward compatibility)
      this.route.queryParams.subscribe((params) => {
        const tripsData = params['trips'];
        const vendorData = params['vendor'];

        if (tripsData) {
          try {
            this.selectedTrips = JSON.parse(decodeURIComponent(tripsData));
            console.log(
              'Bill generation - Data received from query params:',
              this.selectedTrips
            );
          } catch (error) {
            console.error('Error parsing trips data:', error);
            this.selectedTrips = [];
          }
        }

        if (vendorData) {
          try {
            this.vendorDetails = JSON.parse(decodeURIComponent(vendorData));
          } catch (error) {
            console.error('Error parsing vendor data:', error);
          }
        }
      });
    }

    this.calculateSubtotal();
  }

  calculateSubtotal(): void {
    this.subtotal = this.selectedTrips.reduce((total, trip) => {
      return total + (trip.vehicleDetails?.cost || 0);
    }, 0);
    this.calculateTotalAmount();
  }

  calculateTotalAmount(): void {
    const addonsTotal = this.addons.reduce(
      (total, addon) => total + addon.amount,
      0
    );
    const discountsTotal = this.discounts.reduce(
      (total, discount) => total + discount.amount,
      0
    );

    this.totalAmount = this.subtotal + addonsTotal - discountsTotal;
  }

  addAddon(): void {
    if (this.addonForm.valid) {
      const addon: AddonItem = {
        id: Date.now().toString(),
        name: this.addonForm.get('name')?.value,
        amount: Number(this.addonForm.get('amount')?.value) || 0,
      };

      this.addons.push(addon);
      this.addonForm.reset({ name: '', amount: 0 });
      this.calculateTotalAmount();

      this.snackBar.open('Add-on added successfully', 'Close', {
        duration: 3000,
        horizontalPosition: 'end',
        verticalPosition: 'top',
      });
    }
  }

  removeAddon(addonId: string): void {
    this.addons = this.addons.filter((addon) => addon.id !== addonId);
    this.calculateTotalAmount();

    this.snackBar.open('Add-on removed successfully', 'Close', {
      duration: 3000,
      horizontalPosition: 'end',
      verticalPosition: 'top',
    });
  }

  addDiscount(): void {
    if (this.discountForm.valid) {
      const discount: DiscountItem = {
        id: Date.now().toString(),
        name: this.discountForm.get('name')?.value,
        amount: Number(this.discountForm.get('amount')?.value) || 0,
      };

      this.discounts.push(discount);
      this.discountForm.reset({ name: '', amount: 0 });
      this.calculateTotalAmount();

      this.snackBar.open('Discount added successfully', 'Close', {
        duration: 3000,
        horizontalPosition: 'end',
        verticalPosition: 'top',
      });
    }
  }

  removeDiscount(discountId: string): void {
    this.discounts = this.discounts.filter(
      (discount) => discount.id !== discountId
    );
    this.calculateTotalAmount();

    this.snackBar.open('Discount removed successfully', 'Close', {
      duration: 3000,
      horizontalPosition: 'end',
      verticalPosition: 'top',
    });
  }

  onBack(): void {
    this.router.navigate(['/BillingManagement/trip-summary'], {
      state: {
        selectedTrips: this.selectedTrips,
        vendor: this.vendorDetails,
        filters: this.filters,
      },
    });
  }

  generateBill(): void {
    if (this.isGenerating) return;

    this.isGenerating = true;

    // Prepare invoice data for API
    const invoiceData: InvoiceRequest = {
      bills: this.selectedTrips.map((trip) => ({
        billingId: trip.billingId || trip.tripId || '',
        amount: trip.vehicleDetails?.cost || 0,
        description: `Trip from ${trip.routeName || 'N/A'} - ${
          trip.driverDetails?.driverName || 'N/A'
        }`,
      })),
      totalAmount: this.totalAmount,
      paidTo: {
        vendorId:
          this.vendorDetails?.id ||
          this.selectedTrips[0]?.vehicleDetails?.vendorId ||
          '',
        vendorName: this.vendorDetails?.organizationName || 'N/A',
        bankDetails: {
          bankName: this.vendorDetails?.bankDetails?.bankName || 'N/A',
          accountNumber:
            this.vendorDetails?.bankDetails?.accountNumber || 'N/A',
          ifscCode: this.vendorDetails?.bankDetails?.ifscCode || 'N/A',
          accountHolder:
            this.vendorDetails?.bankDetails?.accountHolderName || 'N/A',
        },
      },
      extras: [
        ...this.addons.map((addon) => ({
          type: 'ADDON' as const,
          description: addon.name,
          amount: addon.amount,
        })),
        ...this.discounts.map((discount) => ({
          type: 'DISCOUNT' as const,
          description: discount.name,
          amount: discount.amount,
        })),
      ],
    };

    // Call the invoice API
    this.billingService.createInvoice(invoiceData).subscribe({
      next: (response) => {
        this.isGenerating = false;

        if (
          response.statusCode === 201 &&
          response.data &&
          response.data.length > 0
        ) {
          const invoiceId = response.data[0].invoiceId;

          this.snackBar.open(
            `Invoice created successfully! Invoice ID: ${invoiceId}`,
            'Close',
            {
              duration: 5000,
              horizontalPosition: 'end',
              verticalPosition: 'top',
            }
          );

          // Navigate to generated bill page with invoice data
          const billData = {
            billId: `INV-${invoiceId}`,
            trips: this.selectedTrips,
            vendor: this.vendorDetails,
            subtotal: this.subtotal,
            addons: this.addons,
            discounts: this.discounts,
            totalAmount: this.totalAmount,
            generatedAt: new Date().toISOString(),
            invoiceId: invoiceId,
            apiResponse: response.data[0],
          };

          this.router.navigate(['/BillingManagement/generated-bill'], {
            state: {
              billData: billData,
            },
          });
        } else {
          this.snackBar.open('Failed to create invoice', 'Close', {
            duration: 3000,
            horizontalPosition: 'end',
            verticalPosition: 'top',
          });
        }
      },
      error: (error) => {
        this.isGenerating = false;
        console.error('Error creating invoice:', error);

        this.snackBar.open(
          'Error creating invoice. Please try again.',
          'Close',
          {
            duration: 3000,
            horizontalPosition: 'end',
            verticalPosition: 'top',
          }
        );
      },
    });
  }

  formatCurrency(amount: number): string {
    return `₹${amount.toLocaleString()}`;
  }

  get totalTrips(): number {
    return this.selectedTrips.length;
  }
}
